import type { ReactNode } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { STATUS } from '../../lib/viz';

/**
 * A single headline number. Deliberately not a one-bar bar chart: when the data is
 * one current value, a stat tile reads faster and takes less room.
 */
export function StatTile({
  label, value, delta, deltaLabel, status, icon, hint,
}: {
  label: string;
  value: string;
  /** Percentage change; sign drives the arrow. */
  delta?: number;
  deltaLabel?: string;
  status?: keyof typeof STATUS;
  icon?: ReactNode;
  hint?: string;
}) {
  const positive = (delta ?? 0) >= 0;
  return (
    <div className="surface relative overflow-hidden p-5">
      <div className="flex items-start justify-between gap-3">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">{label}</span>
        {icon && <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-bronze-50 text-bronze-600">{icon}</span>}
      </div>
      <div className="mt-2 flex items-baseline gap-2.5">
        <span className="display text-[34px] leading-none text-ink-900" style={status ? { color: STATUS[status] } : undefined}>
          {value}
        </span>
        {delta !== undefined && (
          <span className="flex items-center gap-0.5 text-[12px] font-medium" style={{ color: positive ? STATUS.good : STATUS.critical }}>
            {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      {(deltaLabel || hint) && <p className="mt-2 text-[11.5px] text-ink-500">{deltaLabel ?? hint}</p>}
    </div>
  );
}
